import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal, Select } from 'antd';
import { Paperclip, X } from 'lucide-react';

import { useIsMobile } from 'core/hooks/Layout';

import { useStaffUsers } from 'core/hooks';

import { matchesPersonQuery } from 'core/utils/personName';
import { ICreateTaskDraft, ITaskSubject, PRIORITIES } from './taskBuckets';
import { PRIORITY_ICONS } from './taskIcons';
import { IPickedLink, TaskLinkPicker } from './TaskLinkPicker';
import { DescriptionField } from './DescriptionField';
import { TaskDatePicker } from './TaskDatePicker';
import { AssigneeAvatar } from './AssigneeAvatar';
import * as M from './TaskModal.styled';
import * as S from './TaskBoard.styled';

/**
 * Что получает место под умные чипы (часть B ТЗ 0109): набранное название и
 * текущие связи формы. Уверенные совпадения часть B добавит сама через
 * `onLinks`, неуверенные — покажет серыми «Связать?».
 */
export interface ITitleAddonContext {
  title: string;
  links: IPickedLink[];
  onLinks: (next: IPickedLink[]) => void;
  /**
   * Зарегистрировать «дожать подсказки» перед созданием (ревью 0109):
   * человек жмёт «Добавить» раньше, чем прошла пауза запроса, — уверенные
   * связи иначе не успели бы прицепиться. Функция отдаёт итоговые связи.
   * `null` — снять регистрацию.
   */
  registerSettle: (fn: (() => Promise<IPickedLink[]>) | null) => void;
}

/** Сколько ждём подсказки при отправке: дольше — создаём без них. */
export const SETTLE_TIMEOUT_MS = 1500;

interface ITaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  /** `openAfter` — открыть созданную задачу («Создать и открыть»). */
  onCreate: (
    draft: ICreateTaskDraft,
    openAfter?: boolean,
    files?: File[],
  ) => Promise<boolean>;
  adding: boolean;
  /** Fixed group scope (group tab) — hides the subject picker. */
  groupId?: string;
  /** Fixed student scope (student tab) — hides the subject picker. */
  studentId?: string;
  /** Fixed teacher scope (teacher tab) — hides the subject picker. */
  teacherId?: string;
  /** Default group when none is seeded (e.g. the command center's rail). */
  defaultGroupId?: string;
  /**
   * Имя этой группы. Без него чип предвыбранной связи печатает сам
   * идентификатор: пикер берёт подпись из своей памяти имён, а она после
   * подстановки извне пуста.
   */
  defaultGroupName?: string;
  /** Готовая строка «где создаём» — «Мои задачи», «Группа PY01». */
  context?: string;
  /**
   * Хозяин умеет ОТКРЫТЬ созданную задачу. Палитра команд, например, не
   * умеет: там окно живёт само по себе, доски под ним нет. Кнопка «Создать и
   * открыть», которая создаёт и не открывает, хуже отсутствующей.
   */
  canOpenAfter?: boolean;
  /**
   * Место под умные чипы связей ПОД названием (часть B ТЗ 0109). Пока пропа
   * нет — места нет, запросов нет.
   */
  titleAddon?: (ctx: ITitleAddonContext) => React.ReactNode;
}

/**
 * Окно создания задачи (ТЗ 0065, упрощено в 0109).
 *
 * ТЗ 0109: название, срок (просто календарь), исполнитель, описание (простой
 * текст), файлы и связи. Статуса здесь нет: новая задача всегда
 * «Запланировано». Приоритет (Обычный / Высокий) вернули в ТЗ 0111.
 *
 * 🔴 Раскрывашки «Дополнительно» здесь БОЛЬШЕ НЕТ. Под ней лежали описание,
 * исполнитель и связи — то есть всё, кроме названия и срока; аудит
 * справедливо назвал это «важные поля спрятаны». Быстрый случай («позвонить
 * Ане») закрывает теперь не она, а строка создания прямо в колонке доски: там
 * название + Enter и ни одного лишнего поля. Раз есть быстрый путь, полная
 * форма имеет право быть полной.
 *
 * Контролы — antd, как в окне задачи. До 0065 создание было собрано на голых
 * `input/select/textarea`, а правка — на antd: два окна об одной сущности
 * выглядели как два разных продукта, причём нативный `input type="date"`
 * показывал «dd/mm/yyyy» посреди русского интерфейса.
 */
export const TaskCreateModal = memo(
  ({
    open,
    onClose,
    onCreate,
    adding,
    groupId,
    studentId,
    teacherId,
    defaultGroupId,
    defaultGroupName,
    context,
    canOpenAfter = false,
    titleAddon,
  }: ITaskCreateModalProps) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    // On a scoped board (group / student / teacher tab) the subject is fixed by
    // the host page, so the picker is hidden entirely.
    const scoped = Boolean(groupId || studentId || teacherId);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    // Файлы к ещё не созданной задаче. Уходят вместе с ней одной мутацией.
    const [files, setFiles] = useState<File[]>([]);
    const fileRef = useRef<HTMLInputElement | null>(null);
    // Связи вместо «предмета» (ревизия 0064): в карточке видов двенадцать,
    // а при создании было три — чтобы прицепить курс или оператора,
    // приходилось создать задачу и открыть её заново.
    const [links, setLinks] = useState<IPickedLink[]>([]);
    const [formDue, setFormDue] = useState('');
    const [formAssigneeId, setFormAssigneeId] = useState('');
    // Приоритет вернулся (ТЗ 0111): «Обычный» по умолчанию.
    const [priority, setPriority] = useState('normal');

    /**
     * Сброс формы на каждом открытии.
     *
     * Механизма «seed» здесь больше нет: колонки доски создают задачу СТРОКОЙ
     * НА МЕСТЕ (ТЗ 0065), а не открывают это окно, и после той правки
     * `openCreate` перестал вызываться с аргументом вовсе. Тип
     * `ITaskCreateSeed` при этом продолжал жить и обещать в доках подстановку
     * срока и статуса из колонки — обещание, которого никто не исполнял, и
     * ловушку впридачу: `seed.status = 'done'` не совпал бы ни с одним
     * сегментом, и форма показала бы пустой выбор при непустом статусе.
     */
    useEffect(() => {
      if (!open) return;
      setTitle('');
      setDescription('');
      setFormAssigneeId('');
      setFormDue('');
      setPriority('normal');
      setFiles([]);
      setLinks(
        defaultGroupId
          ? [
              {
                kind: 'group',
                id: defaultGroupId,
                name: defaultGroupName || defaultGroupId,
              },
            ]
          : [],
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const { staffUsers } = useStaffUsers(false);
    const settleRef = useRef<(() => Promise<IPickedLink[]>) | null>(null);
    const registerSettle = useCallback(
      (fn: (() => Promise<IPickedLink[]>) | null) => {
        settleRef.current = fn;
      },
      [],
    );
    // Двойной Enter, пока ждём подсказки, не создаёт две задачи.
    const submitting = useRef(false);

    // Задача без связей — законное личное дело (ТЗ 0014), так что связи
    // ничего не требуют: единственное обязательное поле — название.
    const canCreate = Boolean(title.trim());

    const submit = useCallback(
      async (openAfter: boolean) => {
        if (!canCreate || adding || submitting.current) return;
        submitting.current = true;
        try {
          // Подсказки по названию ещё в пути — дожидаемся (не дольше 1,5 с)
          // и берём связи с уже прицепленными уверенными совпадениями.
          let finalLinks = links;
          const settle = settleRef.current;
          if (!scoped && settle) {
            finalLinks = await Promise.race([
              settle().catch(() => links),
              new Promise<IPickedLink[]>((resolve) =>
                window.setTimeout(() => resolve(links), SETTLE_TIMEOUT_MS),
              ),
            ]);
          }
          // На скоупленной доске предмет задаёт страница-хозяин.
          let scopedSubject: ITaskSubject = { kind: 'group', id: groupId };
          if (teacherId) scopedSubject = { kind: 'teacher', id: teacherId };
          else if (studentId)
            scopedSubject = { kind: 'student', id: studentId };
          const ok = await onCreate(
            {
              title: title.trim(),
              description: description.trim() || undefined,
              subject: scoped ? scopedSubject : undefined,
              links: scoped
                ? undefined
                : finalLinks.map((row) => ({ kind: row.kind, id: row.id })),
              assigneeId: formAssigneeId || undefined,
              dueDate: formDue || undefined,
              priority,
            },
            openAfter,
            files,
          );
          if (ok) onClose();
        } finally {
          submitting.current = false;
        }
      },
      [
        canCreate,
        adding,
        onCreate,
        title,
        description,
        files,
        scoped,
        links,
        groupId,
        studentId,
        teacherId,
        formAssigneeId,
        formDue,
        priority,
        onClose,
      ],
    );

    return (
      <Modal
        visible={open}
        centered
        width={860}
        style={{ maxWidth: 'calc(100vw - 32px)' }}
        // Как у карточки задачи: тело прокручивается, окно не упирается в
        // края экрана (на 760 px по высоте форма иначе вставала на 4 px от верха).
        // ТЗ 0121: на телефоне окно на весь экран (mobile.css), и тело
        // тянется до низа само; потолок высоты оставлял бы пустую полосу.
        bodyStyle={
          isMobile
            ? undefined
            : { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }
        }
        title={t('pages.tasks.createModalTitle')}
        onCancel={onClose}
        destroyOnClose
        footer={
          <M.Footer>
            {context ? <M.FooterNote>{context}</M.FooterNote> : null}
            <Button onClick={onClose}>{t('button.cancel')}</Button>
            {canOpenAfter ? (
              <Button
                disabled={!canCreate}
                loading={adding}
                onClick={() => submit(true)}
              >
                {t('pages.tasks.create.andOpen')}
              </Button>
            ) : null}
            <Button
              type="primary"
              disabled={!canCreate}
              loading={adding}
              onClick={() => submit(false)}
            >
              {t('pages.tasks.add')}
            </Button>
          </M.Footer>
        }
      >
        <M.Section>
          <S.DrawerLabel>
            {t('pages.communications.taskTitleLabel')}
          </S.DrawerLabel>
          <Input
            autoFocus
            value={title}
            maxLength={255}
            placeholder={t('pages.dashboard.taskTitlePlaceholder')}
            onChange={(e) => setTitle(e.target.value)}
            onPressEnter={() => submit(false)}
          />
        </M.Section>

        {/* Часть B ТЗ 0109: умные чипы связей по набранному названию. */}
        {titleAddon && !scoped ? (
          <M.SmartSlot>
            {titleAddon({
              title,
              links,
              onLinks: setLinks,
              registerSettle,
            })}
          </M.SmartSlot>
        ) : null}

        <M.CreateGrid>
          <div>
            <S.DrawerLabel>{t('pages.tasks.drawer.due')}</S.DrawerLabel>
            {/* Просто календарь (ТЗ 0109): чипов «Сегодня/Завтра/Без срока»
                больше нет, снять срок — крестик пикера. */}
            <TaskDatePicker
              value={formDue || null}
              ariaLabel={t('pages.tasks.drawer.due')}
              placeholder={t('pages.tasks.drawer.noDue')}
              onChange={(next) => setFormDue(next || '')}
            />
          </div>
          <div>
            <S.DrawerLabel>{t('pages.tasks.assignee')}</S.DrawerLabel>
            <Select
              showSearch
              style={{ width: '100%' }}
              value={formAssigneeId || ''}
              aria-label={t('pages.tasks.assignee')}
              // Слова в любом порядке (ТЗ 0111).
              filterOption={(input, option) =>
                matchesPersonQuery(String(option?.title || ''), input)
              }
              onChange={(value: string) => setFormAssigneeId(value)}
            >
              <Select.Option value="" title={t('pages.tasks.assigneeMe')}>
                {t('pages.tasks.assigneeMe')}
              </Select.Option>
              {staffUsers.map((user) => (
                <Select.Option
                  key={user.id}
                  value={user.id}
                  title={user.name || user.email}
                >
                  <M.OptionRow>
                    <AssigneeAvatar
                      id={user.id}
                      name={user.name || user.email}
                      size={20}
                    />
                    {user.name || user.email}
                  </M.OptionRow>
                </Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <S.DrawerLabel id="task-create-priority">
              {t('pages.tasks.priority.label')}
            </S.DrawerLabel>
            <M.SegGroup
              $cols={PRIORITIES.length}
              role="group"
              aria-labelledby="task-create-priority"
            >
              {PRIORITIES.map((key) => {
                const Icon = PRIORITY_ICONS[key];
                return (
                  <M.Seg
                    key={key}
                    type="button"
                    $active={priority === key}
                    aria-pressed={priority === key}
                    onClick={() => setPriority(key)}
                  >
                    {Icon ? (
                      <Icon size={12} strokeWidth={2} aria-hidden="true" />
                    ) : null}
                    {t(`pages.tasks.priority.${key}`)}
                  </M.Seg>
                );
              })}
            </M.SegGroup>
          </div>
        </M.CreateGrid>

        <M.Section>
          <S.DrawerLabel>
            {t('pages.communications.taskDescriptionLabel')}
          </S.DrawerLabel>
          {/* Тот же компонент, что и в окне задачи: простой текст (ТЗ 0109). */}
          <DescriptionField
            value={description}
            ariaLabel={t('pages.communications.taskDescriptionLabel')}
            placeholder={t('pages.tasks.descriptionPlaceholder')}
            onChange={setDescription}
            onPasteFiles={(picked) => setFiles((prev) => [...prev, ...picked])}
          />
        </M.Section>

        <M.Section>
          <S.DrawerLabel>{t('pages.tasks.files.title')}</S.DrawerLabel>
          {/* Файлы уходят ОДНОЙ мутацией вместе с задачей: отказ на файле
              откатывает и её, поэтому «задачи без своих файлов» не бывает. */}
          <M.FileZone
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              setFiles((prev) => [
                ...prev,
                ...Array.from(event.dataTransfer?.files || []),
              ]);
            }}
          >
            {files.map((file, index) => (
              <M.FileRow key={`${file.name}:${file.size}`}>
                <Paperclip size={13} aria-hidden="true" />
                <M.FileMeta>{file.name}</M.FileMeta>
                <M.FileActions>
                  <Button
                    type="text"
                    size="small"
                    aria-label={t('pages.tasks.files.remove')}
                    icon={<X size={13} aria-hidden="true" />}
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, i) => i !== index))
                    }
                  />
                </M.FileActions>
              </M.FileRow>
            ))}
            <Button
              type="dashed"
              size="small"
              icon={<Paperclip size={13} aria-hidden="true" />}
              onClick={() => {
                const node = fileRef.current;
                if (!node) return;
                node.value = '';
                node.click();
              }}
            >
              {t('pages.tasks.files.add')}
            </Button>
            <M.HiddenFileInput
              ref={fileRef}
              type="file"
              multiple
              onChange={(event) =>
                setFiles((prev) => [
                  ...prev,
                  ...Array.from(event.target.files || []),
                ])
              }
            />
          </M.FileZone>
        </M.Section>

        {!scoped ? (
          <M.Section>
            <S.DrawerLabel>{t('pages.tasks.links.filterLabel')}</S.DrawerLabel>
            <M.AddLinkRow>
              <TaskLinkPicker
                value={links}
                onChange={setLinks}
                disabled={adding}
              />
            </M.AddLinkRow>
          </M.Section>
        ) : null}
      </Modal>
    );
  },
);

TaskCreateModal.displayName = 'TaskCreateModal';
